import { useQuery } from '@tanstack/react-query';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMedicalProfessionalPatients } from '~/services/medical-professionals';
import { formatDateIfValid } from '~/utils';
import { Pagination } from '../Pagination';

interface VerifiedPatient {
  id: number;
  name: string;
  hipaa: boolean;
  status: string;
  verifyExpensesStatus: string;
  verifyExpensesDate: Date;
  verifyExpensesMedicalProfessional: string;
  emailed: Date;
  hippa: boolean;
  campaign?: number;
  patient_id?: number;
  campaignStatusCode?: string | null;
}

const columnHelper = createColumnHelper<VerifiedPatient>();

// `clientLinkMode` controls where the Client name link in each row goes.
// - 'campaign' (default): /campaigns/:campaign_id — the staff/outreach
//   view used on /medical-professionals/:id.
// - 'verify': /verify/:patientId — the med-pro-facing verify page used
//   on the med-pro's own dashboard.
type ClientLinkMode = 'campaign' | 'verify';

interface Props {
  id: number;
  clientLinkMode?: ClientLinkMode;
}

// Med pros see Open campaigns, which they can verify, and Closed ones, which
// they can only view (HHLTB-2914). Staff see every status, so the column is
// only shown on the med pro's own dashboard.
const campaignStatusColumn = columnHelper.accessor('campaignStatusCode', {
  header: 'Campaign',
  cell: (info) => {
    const code = info.getValue();
    if (code === 'funded') {
      return (
        <div className='inline-flex rounded-full bg-[#AFC12D] px-3 py-1 text-sm'>
          Open
        </div>
      );
    }
    if (code === 'closed') {
      return (
        <div className='inline-flex rounded-full bg-[#F4F4F5] px-3 py-1 text-sm text-[#131212]'>
          Closed
        </div>
      );
    }
    return '';
  },
});

const buildColumns = (clientLinkMode: ClientLinkMode) => [
  columnHelper.accessor('name', {
    id: 'name',
    header: () => 'Client',
    cell: (info) => {
      const row = info?.row?.original;
      const target =
        clientLinkMode === 'verify'
          ? row?.patient_id
            ? `/verify/${row.patient_id}`
            : '#'
          : row?.campaign
          ? `/campaigns/${row.campaign}`
          : '#';
      return (
        <Link className='font-bold text-primary' to={target}>
          {info.renderValue()}
        </Link>
      );
    },
  }),
  ...(clientLinkMode === 'verify' ? [campaignStatusColumn] : []),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => {
      const raw = info.getValue();
      const value = typeof raw === 'string' ? raw.trim() : '';
      if (!value) {
        return <span className='text-[#949599]'>—</span>;
      }
      const bg_color =
        value === 'Pending'
          ? 'bg-[#FEBF3E]'
          : value === 'Unassigned'
          ? 'bg-[#F4F4F5]'
          : 'bg-[#AFC12D]';
      return (
        <div
          className={`inline-flex rounded-full ${bg_color} px-3 py-1 text-sm hover:bg-opacity-90`}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('verifyExpensesStatus', {
    header: 'Verify Expenses',
    cell: () => (
      <div className='inline-flex rounded-full bg-[#F4F4F5] px-3 py-1 text-sm text-[#131212] hover:bg-opacity-90'>
        Not Verified
      </div>
    ),
  }),
  columnHelper.accessor('emailed', {
    header: clientLinkMode === 'verify' ? 'Date Requested' : 'Emailed',
    cell: (info) =>
      info.row.original.emailed &&
      formatDateIfValid(info.row.original.emailed, 'MM/dd/yy'),
  }),
];

const NeedVerificationTab = ({ id, clientLinkMode = 'campaign' }: Props) => {
  const [pages, setPages] = useState<number>(1);
  const [data, setData] = useState([]);
  const [page, setPage] = useState<number>(1);
  const columns = useMemo(
    () => buildColumns(clientLinkMode),
    [clientLinkMode]
  );
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { data: fetchedData, isLoading } = useQuery({
    queryKey: ['medical-professional-unverified-patients', id, page],
    queryFn: () => fetchMedicalProfessionalPatients(id, 'unverified', page),
  });

  const getPatients = () => {
    if (fetchedData) {
      const data = fetchedData?.results?.map((patient: any) => ({
        id: patient?.person?.id,
        patient_id: patient?.id,
        name: `${patient?.person?.first_name} ${patient?.person?.last_name}`,
        hippa: true,
        status: patient?.report_verification_status_code,
        verifyExpensesStatus: 'Not Verified',
        verifyExpensesMedicalProfessional:
          patient?.medical_professional?.person?.first_name,
        emailed: patient?.email_sent_to_medical_professional_date,
        campaign: patient?.campaign_ref,
        campaignStatusCode: patient?.campaign_status_code,
      }));
      setData(data);
      const total = fetchedData?.count;
      if (total) {
        setPages(Math.ceil(total / 20));
      }
    }
  };

  useEffect(() => {
    getPatients();
  }, [fetchedData, isLoading]);

  return (
    <>
      <table className='datatable-table datatable-one w-full table-auto border-collapse overflow-hidden break-words px-4 md:table-fixed md:overflow-auto md:px-8'>
        <thead className='border-separate px-4'>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              className='border-t border-stroke bg-whiten'
              style={{ fontSize: '14px' }}
              key={headerGroup.id}
            >
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={{ paddingTop: '16px', paddingBottom: '16px' }}
                >
                  <div className='flex items-center'>
                    <span>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {isLoading
            ? Array.from({ length: 10 }).map((_, index) => (
                <tr key={index}>
                  {columns.map((_column, colIndex) => (
                    <td
                      key={colIndex}
                      className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                    >
                      <div className='max-w-sm animate-pulse'>
                        <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            : table.getRowModel().rows.map((row) => (
                <tr className='border-t border-stroke' key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      style={{ paddingTop: '16px', paddingBottom: '16px' }}
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>

      <Pagination page={page} pages={pages} setPage={setPage} totalCount={fetchedData?.count} />
    </>
  );
};

export default NeedVerificationTab;
